import { useEffect } from 'react';
import { authClient } from '@/auth';
import { refreshSocketAuth } from '@/lib/socket';

/**
 * Keeps the realtime socket's identity in step with the auth session.
 *
 * Subscribes to the auth client's own state listener — NOT a React hook — so
 * the re-handshake happens at the exact moment the api access token is stored
 * (signup/login exchange done) or cleared (logout). The React-rendered
 * `isAuthenticated` can flip a beat before the token is actually persisted
 * (Firebase settles first, the api exchange follows), which is how the socket
 * previously re-handshook with an empty token after signup.
 *
 * `refreshSocketAuth` no-ops when the identity didn't change, so extra listener
 * firings (isLoading flickers, token rotations) cost nothing. Mount once, next
 * to the other realtime hooks.
 */
export function useSocketAuthSync(): void {
  useEffect(() => {
    // Catch up once on mount (a session restored before this subscribed)…
    refreshSocketAuth();
    // …then follow every auth-state change; returns its own unsubscribe.
    return authClient.onAuthStateChange(() => refreshSocketAuth());
  }, []);
}
